'use client';

/**
 * Expanded sidebar (296px) — Phase 2.
 *
 * Composition (top → bottom):
 *   - Brand + provider chip + collapse button
 *   - Cmd-K trigger (delegates to existing palette in Phase 3)
 *   - PINNED list (drag-reorder, right-click context, persisted via
 *     useUserPreferences('nav-v2:pins'))
 *   - BROWSE — compact domain list
 *   - Footer — Settings / Help buttons
 *
 * Critical filter chain (4 gates) — applied here so every consumer
 * inherits the same authoritative visibility:
 *   1. itemMatchesPrograms(item, providerType) — RAC/SAH/MIXED scoping
 *   2. liveRoleMatchesAlias(liveRole, item.roles) — alias first pass
 *   3. isRoleAllowedForPath(href, liveRole) — live RBAC (AUTHORITATIVE)
 *   4. isModuleNavigationVisible(href, enabledModules) — entitlement gate
 *
 * Spec §0 hard constraint #2 + #3 say the live (3) and (4) gates are
 * authoritative — the alias and program signals are coarse first
 * passes. An item that passes (1)+(2) but fails (3) or (4) MUST be
 * hidden.
 */

import { useMemo } from 'react';
import { isPayrollStatusReviewer } from '@/lib/payroll/coordinator-status';
import { useComplianceObligations } from '@/hooks/useComplianceObligations';
import { isComplianceNavRouteApplicable } from '@/lib/navigation/compliance-applicability';
import {
  isFactDrivenComplianceSurfaceVisible,
  complianceSurfaceForHref,
  type ComplianceSurfaceVisibility,
} from '@/lib/compliance/hub-tile-visibility';
import { useComplianceSurfaceVisibility } from '@/lib/compliance/use-surface-visibility';
import Link from 'next/link';
import { PanelLeftClose, Search, Settings, CircleHelp, MessageCircle } from 'lucide-react';
import { buttonVariants, Logo, CountBadge, controlSizeClassName } from '@statura/ui';
import { trackNavV2Event, type NavV2EventSource } from '@/lib/nav-v2/telemetry';
import {
  FOOTER_NAV_V2,
  NAV_V2,
  COMPLIANCE_ONLY_NAV_GROUPS,
  type NavBadgeCounts,
  type NavV2Group,
  type NavV2Item,
  type ProviderType,
} from '@/lib/navigation/nav-v2';
import { isHrefVisibleForProviderProgram } from '@/lib/navigation/program-route-policy';
import { liveRoleMatchesAlias } from '@/lib/navigation/role-aliases';
import { isRoleAllowedForPath } from '@/lib/route-access';
import { isModuleNavigationVisible } from '@/lib/module-access';
import { isDashboardProductionSurfaceVisibleForTenant } from '@/lib/production-surface-access';
import { isLaunchPrimaryNavigationVisible } from '@/lib/launch-navigation';
import { isTenantNavigationPathVisible } from '@/lib/navigation/tenant-navigation';
import { isFrontlineSelfServiceNavigationVisible } from '@/lib/navigation/frontline-self-service';
import { useGovernmentIntegrations } from '@/contexts/government-integration-context';
import {
  isGovernmentIntegrationPathVisible,
  type GovernmentIntegrationEnablement,
} from '@/lib/integrations/government/enablement-types';
import { isMvpPolishL1HrefAllowed } from '@/lib/navigation/mvp-polish-l1-allowlist';
import type { VisibleGoLiveScope } from '@/lib/billing';
import { isComplianceOnlyPlan } from '@/lib/billing';
import type { ProviderProgramContext } from '@/lib/navigation/provider-type';
import { ProviderChip } from './provider-chip';
import { DomainTiles } from './domain-tiles';
import { PinnedList } from './pinned-list';

const SIDEBAR_WIDTH = 296;

const FULL_COMPLIANCE_NAV_ROLES = new Set([
  'TENANT_ADMIN',
  'COMPLIANCE_OFFICER',
  'QUALITY_OFFICER',
]);
const isExpandedCompliancePersona = (
  role: string | null | undefined,
  plan: string | null | undefined,
) => isComplianceOnlyPlan(plan) && FULL_COMPLIANCE_NAV_ROLES.has(role ?? '');

// Coordinator keeps its current leaf curation; empty cards still disappear.
const usesComplianceCards = (plan?: string | null, role?: string | null) =>
  isComplianceOnlyPlan(plan) &&
  (FULL_COMPLIANCE_NAV_ROLES.has(role ?? '') || role === 'COORDINATOR');

export function sidebarGroupsForBillingPlan(
  billingPlan?: string | null,
  liveRole?: string | null,
): readonly NavV2Group[] {
  if (!usesComplianceCards(billingPlan, liveRole)) return NAV_V2;
  return [
    ...NAV_V2.filter(
      (group) => group.id !== 'quality' && group.id !== 'governance' && group.id !== 'workforce',
    ),
    ...COMPLIANCE_ONLY_NAV_GROUPS.map((group) =>
      group.id === 'compliance-people'
        ? {
            ...group,
            items: [
              ...(NAV_V2.find((candidate) => candidate.id === 'workforce')?.items ?? []),
              ...group.items,
            ],
          }
        : group,
    ),
  ];
}

export interface NavV2SidebarProps {
  /** Pathname for active-state. */
  currentPath: string;
  /** Live `users.role`. */
  liveRole: string | null | undefined;
  /** Tenant provider type. */
  providerType: ProviderType;
  /** Server-resolved current programme family and selectable exact lenses. */
  programContext?: ProviderProgramContext;
  /** Tenant-enabled module paths (from ModuleContext). `null` = legacy all-access. */
  enabledModules: string[] | null;
  payrollRebuildOn?: boolean;
  billingPlan?: string | null;
  /** Tenant id for tenant-scoped UAT production-surface overrides. */
  tenantId?: string | null;
  /** True only for DB-marked demo tenants. */
  isDemoTenant?: boolean;
  /** Active launch profile, when this tenant is in RAC/SAH/HR go-live mode. */
  visibleGoLiveScope?: VisibleGoLiveScope;
  /** Tenant-level navigation curation applied after hard gates. */
  tenantHiddenNavigationPaths?: readonly string[];
  frontlineSelfServiceOnly?: boolean;
  /** Ordered list of pinned hrefs. */
  pinnedHrefs: readonly string[];
  /** Real, already-scoped nav badge counts keyed by NavBadgeKey. */
  badgeCounts?: NavBadgeCounts;
  /** Reorder pins. */
  onReorderPins: (next: string[]) => void;
  /** Toggle pin. */
  onTogglePin: (href: string) => void;
  /** Open Cmd-K palette. */
  onCmdK: () => void;
  /** Collapse the sidebar to the 64px rail. */
  onCollapse: () => void;
  /** Open the help chat panel. */
  onHelpChat?: () => void;
  /** True when rendered inside the mobile drawer — hides the desktop collapse button. */
  inMobileDrawer?: boolean;
}

/**
 * The 4-gate filter — extracted so the same predicate runs over both
 * groups and pinned-href validation.
 */
export function navV2ItemIsVisible(
  item: Pick<
    NavV2Item,
    'href' | 'roles' | 'additionalLiveRoles' | 'programs' | 'payrollStatusOnly'
  >,
  ctx: {
    liveRole: string | null | undefined;
    billingPlan?: string | null;
    providerType: ProviderType;
    providerProgramContext?: ProviderProgramContext;
    enabledModules: string[] | null;
    payrollRebuildOn?: boolean;
    tenantId?: string | null;
    isDemoTenant?: boolean;
    visibleGoLiveScope?: VisibleGoLiveScope;
    tenantHiddenNavigationPaths?: readonly string[];
    frontlineSelfServiceOnly?: boolean;
    governmentIntegrations?: GovernmentIntegrationEnablement;
    complianceSurfaceVisibility?: ComplianceSurfaceVisibility | null;
  },
): boolean {
  const pathname = item.href.split(/[?#]/, 1)[0] ?? item.href;
  return (
    (!item.payrollStatusOnly ||
      (ctx.payrollRebuildOn === true && isPayrollStatusReviewer(ctx.liveRole))) &&
    isFactDrivenComplianceSurfaceVisible(pathname, ctx.complianceSurfaceVisibility) &&
    (complianceSurfaceForHref(pathname)
      ? true
      : isHrefVisibleForProviderProgram(
          pathname,
          ctx.providerProgramContext ?? ctx.providerType,
          item.programs,
          ctx.tenantId,
        )) &&
    (liveRoleMatchesAlias(ctx.liveRole, item.roles) ||
      Boolean(ctx.liveRole && item.additionalLiveRoles?.includes(ctx.liveRole))) &&
    isRoleAllowedForPath(pathname, ctx.liveRole, ctx.billingPlan) &&
    isModuleNavigationVisible(pathname, ctx.enabledModules, {
      isDemoTenant: ctx.isDemoTenant,
    }) &&
    // Phase 2 retro audit P1: pages can be hidden by additional shell
    // gates beyond role + module — production-blocked enterprise
    // pages, subscription-blocked routes, plan-exempt allowlists.
    // `isDashboardProductionSurfaceVisible` mirrors the page-shell's
    // own availability gate (same predicate used by command-palette
    // and module-gate's launch-surface logic). Without this gate the
    // sidebar surfaced links that the page itself would immediately
    // reject — broken-link UX for opted-in users.
    isDashboardProductionSurfaceVisibleForTenant(pathname, {
      tenantId: ctx.tenantId,
      isDemo: ctx.isDemoTenant,
    }) &&
    isLaunchPrimaryNavigationVisible(pathname, ctx.visibleGoLiveScope, {
      isDemoTenant: ctx.isDemoTenant,
    }) &&
    isTenantNavigationPathVisible(pathname, ctx.tenantHiddenNavigationPaths) &&
    isFrontlineSelfServiceNavigationVisible({
      href: pathname,
      role: ctx.liveRole,
      enabled: ctx.frontlineSelfServiceOnly === true,
    }) &&
    isGovernmentIntegrationPathVisible(pathname, ctx.governmentIntegrations, ctx.liveRole)
  );
}

export type NavV2VisibilityCtx = Parameters<typeof navV2ItemIsVisible>[1];

/**
 * Sidebar L1 + footer visibility. Entitlement stays in `navV2ItemIsVisible`
 * (Home tiles, badges, Cmd-K). Coordinator and facility-lead L1 are then
 * cut to their KEEP doors.
 */
/**
 * Home-care lens for the coordinator SAH KEEP doors: a SAH provider, or a
 * workspace lens admitting SAH and not RAC. Mirrors itemMatchesPrograms.
 */
export function isHomeCareSidebarLens(ctx: NavV2VisibilityCtx): boolean {
  const lens = ctx.providerProgramContext;
  if (lens && lens.workspace != null) {
    const firewalls: readonly string[] = lens.admittedFirewalls ?? [];
    return firewalls.includes('SAH') && !firewalls.includes('RAC');
  }
  return ctx.providerType === 'SAH';
}

export function navV2SidebarLeafIsVisible(
  item: Pick<NavV2Item, 'href' | 'roles' | 'programs' | 'payrollStatusOnly'>,
  ctx: NavV2VisibilityCtx,
): boolean {
  return (
    // Review-only payroll discovery belongs under People, never another L1 door.
    !item.payrollStatusOnly &&
    navV2ItemIsVisible(item, ctx) &&
    (isExpandedCompliancePersona(ctx.liveRole, ctx.billingPlan) ||
      isMvpPolishL1HrefAllowed(item.href, ctx.liveRole, isHomeCareSidebarLens(ctx)))
  );
}

export function NavV2Sidebar({
  currentPath,
  liveRole,
  providerType,
  programContext,
  enabledModules,
  payrollRebuildOn,
  billingPlan,
  tenantId,
  isDemoTenant,
  visibleGoLiveScope,
  tenantHiddenNavigationPaths,
  frontlineSelfServiceOnly,
  pinnedHrefs,
  badgeCounts,
  onReorderPins,
  onTogglePin,
  onCmdK,
  onCollapse,
  onHelpChat,
  inMobileDrawer,
}: NavV2SidebarProps) {
  const governmentIntegrations = useGovernmentIntegrations();
  const { resolutionsById } = useComplianceObligations();
  const { data: complianceSurfaceVisibility } = useComplianceSurfaceVisibility();
  // Entitlement gates + coordinator / facility-lead L1 allowlist. Empty groups drop.
  const visibleGroups = useMemo<ReadonlyArray<NavV2Group & { items: readonly NavV2Item[] }>>(() => {
    const baseGroups = sidebarGroupsForBillingPlan(billingPlan, liveRole);
    return baseGroups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) =>
            (complianceSurfaceForHref(item.href.split(/[?#]/, 1)[0] ?? item.href)
              ? isFactDrivenComplianceSurfaceVisible(
                  item.href.split(/[?#]/, 1)[0] ?? item.href,
                  complianceSurfaceVisibility,
                )
              : isComplianceNavRouteApplicable(
                  item.href.split(/[?#]/, 1)[0] ?? item.href,
                  resolutionsById,
                )) &&
            navV2SidebarLeafIsVisible(item, {
              liveRole,
              billingPlan,
              providerType,
              providerProgramContext: programContext,
              enabledModules,
              payrollRebuildOn,
              tenantId,
              isDemoTenant,
              visibleGoLiveScope,
              tenantHiddenNavigationPaths,
              frontlineSelfServiceOnly,
              governmentIntegrations,
              complianceSurfaceVisibility,
            }),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [
    liveRole,
    providerType,
    programContext,
    enabledModules,
    payrollRebuildOn,
    billingPlan,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    governmentIntegrations,
    resolutionsById,
    complianceSurfaceVisibility,
  ]);

  // Visible hrefs set — used to validate pins (drop a pin if the user lost
  // access to it; never break the sidebar with a 404'd pin).
  const visibleHrefsSet = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    for (const g of visibleGroups) for (const it of g.items) s.add(it.href);
    return s;
  }, [visibleGroups]);

  // The shell resolves defaults through the preference reader. Saved pins win.
  const sidebarPins = pinnedHrefs;

  // Pinned set drives the context-menu pin/unpin label across tiles + rows.
  const pinnedSet = useMemo(() => new Set(sidebarPins), [sidebarPins]);

  // Bugbot Phase 2 finding (polish): the "Pinned (n)" badge in the section
  // header must reflect what the user actually sees in the list, not the
  // raw stored count. Persisted pins that are currently hidden by role /
  // module gates are preserved in storage (see PinnedList.handleDragEnd)
  // but shouldn't be counted here.
  const visiblePinnedCount = useMemo(
    () => sidebarPins.filter((h) => visibleHrefsSet.has(h)).length,
    [sidebarPins, visibleHrefsSet],
  );

  // Footer Settings + Help stay (they are on the coordinator / facility KEEP lists).
  const visibleFooter = useMemo(
    () =>
      FOOTER_NAV_V2.filter((item) =>
        navV2SidebarLeafIsVisible(item, {
          liveRole,
          providerType,
          providerProgramContext: programContext,
          enabledModules,
          payrollRebuildOn,
          tenantId,
          isDemoTenant,
          visibleGoLiveScope,
          tenantHiddenNavigationPaths,
          frontlineSelfServiceOnly,
          governmentIntegrations,
          complianceSurfaceVisibility,
        }),
      ),
    [
      liveRole,
      providerType,
      programContext,
      enabledModules,
      payrollRebuildOn,
      tenantId,
      isDemoTenant,
      visibleGoLiveScope,
      tenantHiddenNavigationPaths,
      frontlineSelfServiceOnly,
      governmentIntegrations,
      complianceSurfaceVisibility,
    ],
  );

  // Codex Phase 6 pass 1 P3: when this sidebar is rendered inside the
  // mobile drawer (NavV2MobileDrawer wraps it with inMobileDrawer=true),
  // every click is a tap from the mobile surface — not the desktop
  // sidebar. Source-tag accordingly so per-surface telemetry doesn't
  // count mobile users as desktop sidebar users.
  const itemClickSource: NavV2EventSource = inMobileDrawer ? 'mobile-drawer' : 'sidebar';
  const pinnedClickSource: NavV2EventSource = inMobileDrawer ? 'mobile-drawer' : 'sidebar-pinned';
  const domainClickSource: NavV2EventSource = inMobileDrawer
    ? 'mobile-drawer'
    : 'sidebar-domain-tiles';
  const footerClickSource: NavV2EventSource = inMobileDrawer ? 'mobile-drawer' : 'sidebar-footer';

  return (
    <aside
      aria-label="Workspace navigation"
      style={{
        width: inMobileDrawer ? '100%' : SIDEBAR_WIDTH,
        flex: inMobileDrawer ? '1 1 auto' : `0 0 ${SIDEBAR_WIDTH}px`,
      }}
      className="ds-shell-navigation flex h-full min-h-0 flex-col border-r border-control-border/30 bg-surface-canvas"
      data-nav-v2-sidebar="expanded"
    >
      {/* Header */}
      <div className="px-4.5 pb-2.5 pt-4.5">
        <div className="mb-3.5 flex items-center justify-between gap-2">
          <Link
            href="/overview"
            onClick={() =>
              trackNavV2Event('nav_v2_item_clicked', {
                source: itemClickSource,
                route: '/overview',
                variant: 'logo',
              })
            }
            className="inline-flex shrink-0 items-center rounded-control"
            aria-label="Statura Care home"
          >
            <Logo variant="full" className="h-6" />
          </Link>
          <div className="flex min-w-0 items-center gap-1.5">
            <ProviderChip
              providerType={providerType}
              programContext={programContext}
              tenantId={tenantId}
            />
            {!inMobileDrawer && (
              <button
                type="button"
                onClick={onCollapse}
                aria-label="Collapse sidebar"
                title="Collapse sidebar  ["
                data-ui="button"
                className={buttonVariants({
                  variant: 'secondary',
                  size: 'compact',
                  className:
                    '!p-0 w-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                })}
              >
                <PanelLeftClose className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cmd-K trigger */}
      <div className="px-4.5 pb-3">
        <button
          type="button"
          onClick={onCmdK}
          className={`${controlSizeClassName('default')} flex min-w-0 w-full items-center gap-2 rounded-control border border-control-border/30 bg-surface-sheet text-left text-text-muted hover:bg-surface-sheet/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`}
          aria-label="Open command palette"
          data-nav-v2-cmdk-trigger="true"
        >
          <Search className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">Go to anything…</span>
          <kbd className="shrink-0 rounded-control border border-control-border/30 bg-surface-canvas px-1.5 py-0.5 font-mono text-caption text-text-muted">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Scrolling middle */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%-16px),transparent)]">
        {/* DOMAINS */}
        <SidebarSectionLabel>Browse</SidebarSectionLabel>
        <DomainTiles
          groups={visibleGroups}
          currentPath={currentPath}
          pinnedSet={pinnedSet}
          onTogglePin={onTogglePin}
          badgeCounts={badgeCounts}
          clickSource={domainClickSource}
        />

        <div className="h-4" />

        {/* PINNED */}
        <SidebarSectionLabel count={visiblePinnedCount}>Pinned</SidebarSectionLabel>
        <PinnedList
          pinnedHrefs={sidebarPins}
          groups={visibleGroups}
          visibleHrefs={visibleHrefsSet}
          currentPath={currentPath}
          onReorder={onReorderPins}
          onTogglePin={onTogglePin}
          onCmdK={onCmdK}
          badgeCounts={badgeCounts}
          clickSource={pinnedClickSource}
        />
      </div>

      {/* Footer (Settings + Help + Chat) */}
      {(visibleFooter.length > 0 || onHelpChat) && (
        <div className="flex shrink-0 flex-wrap list-none gap-1.5 border-t border-control-border/30 bg-surface-canvas p-3">
          {visibleFooter.map((item) => {
            const Icon = item.label === 'Settings' ? Settings : CircleHelp;
            const isActive = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() =>
                  trackNavV2Event('nav_v2_item_clicked', {
                    source: footerClickSource,
                    route: item.href,
                  })
                }
                className={[
                  'flex h-11 min-h-11 flex-1 items-center justify-center gap-1.5 before:hidden before:content-none after:hidden after:content-none rounded-control border border-control-border/30 px-2.5 py-1.5 text-body',
                  isActive
                    ? 'bg-selected font-semibold text-selected-foreground'
                    : 'bg-surface-sheet font-medium text-text-muted hover:bg-surface-canvas',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                ].join(' ')}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon
                  className={[
                    'h-3.5 w-3.5',
                    isActive ? 'text-selected-foreground' : 'text-text-muted',
                  ].join(' ')}
                  aria-hidden="true"
                />
                {item.label}
              </Link>
            );
          })}
          {onHelpChat && (
            <button
              type="button"
              onClick={() => {
                trackNavV2Event('nav_v2_item_clicked', {
                  source: footerClickSource,
                  route: 'help-chat',
                });
                onHelpChat();
              }}
              className="flex h-11 min-h-11 flex-1 items-center justify-center gap-1.5 before:hidden before:content-none after:hidden after:content-none rounded-control border border-control-border/30 bg-surface-sheet px-2.5 py-1.5 text-body font-medium text-text-muted hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              aria-label="Open help chat"
            >
              <MessageCircle className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
              Chat
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

function SidebarSectionLabel({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="mb-2 flex items-center justify-between px-1">
      <p className="text-caption font-semibold uppercase tracking-wide text-selected-foreground">
        {children}
      </p>
      {typeof count === 'number' && <CountBadge compact count={count} label="navigation items" />}
    </div>
  );
}
